import { createApp } from 'vue'
import { createPinia } from 'pinia'
import App from './App.vue'
import { router } from './router'
import { initAuth } from './auth/keycloak'
import { applyLocale, i18n } from './i18n'
import { useThemeStore } from './stores/theme'
import './styles/main.css'

/**
 * Authentication is settled before the app mounts, so no screen ever renders in
 * a half-signed-in state and no request races the token.
 */
async function bootstrap(): Promise<void> {
  try {
    await initAuth()
  } catch (error) {
    document.getElementById('app')!.innerHTML = `
      <div style="display:grid;place-items:center;height:100vh;font-family:system-ui;padding:2rem;text-align:center">
        <div>
          <h1 style="font-size:1.25rem;margin-bottom:.5rem">Could not reach the sign-in service</h1>
          <p style="color:#5b6472;max-width:38rem">
            The portal could not contact Keycloak. Check that it is running and that the portal is
            configured with the right realm and client, then reload.
          </p>
        </div>
      </div>`
    console.error('[auth] initialisation failed', error)
    return
  }

  const app = createApp(App)
  app.use(createPinia())
  app.use(i18n)

  // This is a keyboard-driven tool: a screen that opens with one obvious field
  // should put the cursor in it.
  app.directive('focus', {
    mounted: (el: HTMLElement) => el.focus(),
  })

  // Both applied before mount, so the first paint is already in the right
  // theme and already the right way round.
  useThemeStore().apply()
  applyLocale()

  app.use(router)
  await router.isReady()
  app.mount('#app')
}

void bootstrap()
